import { HeaderOptions } from "components/Header/HeaderUI"
import { graphql, Link } from "gatsby"
import { Button } from "nzk-react-components"
import React, { useEffect, useRef } from "react"
import styled from "styled-components"
import getLocales from "utils/getLocales"
import FooterStrapi from "../components/Footer"
import HeaderStrapi from "../components/Header"
import SEO, { getSEOProps } from "../components/SEO"
import DynamicZone from "../components/Strapi/DynamicZone"
import { EU_LOCALES } from "../constants"
import BareLayout from "../layouts/Bare"

const Wrapper = styled.div`
  > :first-child {
    padding-top: 50px;
  }

  > .sticky-cta {
    position: fixed;
    bottom: 0;
    width: 100%;
    padding: 10px 10px;
    background: white;
    z-index: 2;
    display: none;
    justify-content: center;
  }

  @media screen and (max-width: 600px) {
    > .sticky-cta.is-visible.test-enabled {
      display: flex;
    }
  }

  &.with-tp-banner,
  &.sticky-tp {
    > :first-child {
      padding-top: 90px;
    }
  }
`

interface IProps {
  data: {
    site: {
      siteMetadata: { siteUrl: string }
    }
    strapiDynamicPages: Strapi.DynamicPage
  }
  pageContext: {
    defaultLang: string
    slug: string
    pagePath?: string
    noCountrySelector?: boolean
    headerOptions?: HeaderOptions
  }
}

export const updateLocalizations = page => {
  const { locale, localizations } = page
  const locales = [...localizations].map(l => l.locale)
  if (
    EU_LOCALES.indexOf(locale) >= 1 ||
    localizations.filter(l => EU_LOCALES.indexOf(l.locale) >= 1).length >= 1
  ) {
    return {
      locale,
      localizations: [
        ...localizations,
        ...EU_LOCALES.filter(l => locales.indexOf(l) < 0 && l !== locale).map(
          l => ({
            locale: `en-${l}`,
            translated: false,
          })
        ),
      ],
    }
  }
  return { locale, localizations }
}

const DynamicPageTemplate = (props: IProps) => {
  const { data, pageContext }: { data: any; pageContext: any } = props
  const { site, strapiDynamicpages: dynamicpage } = data
  const { content, SEO: SEOProps, customPath } = dynamicpage
  const { locale, localizations = [] } = updateLocalizations({
    ...dynamicpage,
    localizations: [
      ...dynamicpage.localizations,
      { locale: dynamicpage.locale, translated: dynamicpage.translated },
    ],
    locale: pageContext.locale
      ? pageContext.locale.replace("/", "")
      : dynamicpage.locale,
  })
  const otherLangs = getLocales(localizations)
  const nonStickyTrustPilotbanner =
    pageContext.headerOptions?.trustpilot &&
    !pageContext.headerOptions.trustpilot.sticky
  const stickyTrustpilot = Boolean(
    pageContext.headerOptions?.trustpilot?.sticky
  )

  const SHOW_HIDDEN_CTA_BUTTON = props.pageContext.pagePath === "/parents"

  const stickyCtaRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!SHOW_HIDDEN_CTA_BUTTON) return

    const updateStickyCtaVisibility = () => {
      const shown = window.scrollY > window.innerHeight / 2
      if (shown) {
        stickyCtaRef.current?.classList.add('is-visible')
      } else {
        stickyCtaRef.current?.classList.remove('is-visible')
      }
    }

    updateStickyCtaVisibility()
    window.addEventListener("scroll", updateStickyCtaVisibility, {
      passive: true,
    })

    return () => {
      window.removeEventListener("scroll", updateStickyCtaVisibility)
    }
  }, [SHOW_HIDDEN_CTA_BUTTON])

  return (
    <BareLayout>
      <SEO
        {...getSEOProps(SEOProps)}
        lang={locale}
        links={otherLangs.map(l => {
          if (l === props.pageContext.defaultLang || l === "en") {
            return {
              rel: "alternate",
              hrefLang: l === "en" ? "x-default" : l,
              href: `${site.siteMetadata.siteUrl}${customPath ? `${customPath}` : `/${props.pageContext.slug}`
                }`,
            }
          }
          return {
            rel: "alternate",
            hrefLang: l,
            href: `${site.siteMetadata.siteUrl}/${l}${customPath ? `${customPath}` : `/${props.pageContext.slug}`
              }`,
          }
        })}
        canonical={`${site.siteMetadata.siteUrl}${locale === "en" ? "" : `/${locale}`
          }${customPath ? `${customPath}` : `/${props.pageContext.slug}`}`}
        titleTemplate="%s | Night Zookeeper"
      />
      <HeaderStrapi
        availableLangs={props.pageContext.noCountrySelector ? [] : otherLangs}
        locale={locale}
        pagePath={props.pageContext?.pagePath}
        options={props.pageContext?.headerOptions}
      />
      <Wrapper
        className={`content-root ${nonStickyTrustPilotbanner ? "with-tp-banner" : ""
          } ${stickyTrustpilot ? "sticky-tp" : ""}`}
      >
        <DynamicZone content={content} pageContext={props.pageContext} />

        {SHOW_HIDDEN_CTA_BUTTON && (
          <div ref={stickyCtaRef} id='sticky-cta' className='sticky-cta'>
            <Link to='/parents/checkout/plan'>
              <Button>Start Free Trial</Button>
            </Link>
          </div>
        )}
      </Wrapper>
      <FooterStrapi />
    </BareLayout>
  )
}

export const query = graphql`
  query ($id: Int) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    strapiDynamicpages(strapiId: { eq: $id }) {
      locale
      customPath
      translated
      localizations {
        locale
        customPath
        translated
      }
      content {
        ...BookHero
        ...ZooHero
        ...ParentProductHero
        ...TeacherHero
        ...ShopHero
        ...PartnersGroup
        ...ExplanationHero
        ...TitleSeparator
        ...EngagingBanner
        ...BigIconsHero
        ...RatingsHero
        ...HowItWorks
        ...QuoteHero
        ...PlansHero
        ...PlanHero
        ...QuotesCarousel
        ...TeacherCTAHero
        ...PageTitle
        ...FAQSection
        ...Partners
        ...Strapi_RichSection
        ...FixedBanner
        ...CTAStrip
        ...TrustpilotWidget
        ...PlansSelector
        ...LandingPageHero
        __typename
      }
      slug
      SEO {
        title
        description
        url
        noIndex
        image {
          url
        }
        canonical
      }
    }
  }
`

export default DynamicPageTemplate
